import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage } from '@/api/client';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Re-run the loader. Safe to pass straight to an onClick. */
  reload: () => void;
}

/**
 * Load data on mount and expose a reload.
 *
 * The `settled` guard matters more than it looks: these pages reload after every
 * workflow action, and a user who clicks Approve and immediately navigates away
 * would otherwise get a setState on an unmounted component — or worse, a slow
 * first response landing after a fast second one and overwriting it.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const mounted = useRef(true);
  const generation = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const run = ++generation.current;
    setLoading(true);
    setError(null);

    loader()
      .then((result) => {
        if (!mounted.current || run !== generation.current) return;
        setData(result);
      })
      .catch((err: unknown) => {
        if (!mounted.current || run !== generation.current) return;
        setError(errorMessage(err));
      })
      .finally(() => {
        if (!mounted.current || run !== generation.current) return;
        setLoading(false);
      });
    // The loader identity is deliberately not a dependency: callers write it
    // inline, so including it would re-run on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, ...deps]);

  return { data, loading, error, reload };
}
